import {
	Button,
	Checkbox,
	type CheckboxProps,
	type Job,
	Modal,
	TraitementLong,
} from '@mairistem/composants';
import { getErrorMessage } from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import TypeBudget from '../../../../constants/simulation/typeBudget';
import TypePeriode from '../../../../constants/simulation/typePeriode';
import type PeriodeModalProps from '../../../../interfaces/simulation/periodeModalProps';
import type Simulation from '../../../../interfaces/simulation/simulation';
import {
	getMontantSimulationNotSamePropVoteCount,
	propToVote,
	transfertComptabilite,
} from '../../../../services/simulation/simulation';
import getPeriodeContentBudgetText from '../../../../utils/simulation/outil';

const renderPeriodeContent = (simulation: Simulation) => {
	switch (simulation?.typePeriode) {
		case TypePeriode.DEMANDE:
			return (
				<>
					Pour chaque ligne budgétaire, la proposition de l’ordonnateur affichée
					va correspondre au total des demandes acceptées.
					<br />
					Vous pourrez ensuite inscrire de nouvelles propositions ou ajuster le
					montant proposé de ces demandes acceptées.
				</>
			);
		case TypePeriode.PROPOSE:
			return (
				<>
					Par défaut, sur chaque ligne budgétaire, le montant du vote va être
					mis à jour avec le montant de la proposition.
					<br />
					Vous pourrez modifier le montant voté sur les lignes budgétaires pour
					lesquelles l'assemblée délibérante n'a pas adopté la proposition
					budgétaire formulée par l'ordonnateur.
				</>
			);
		case TypePeriode.VOTE: {
			return (
				<>
					{`${getPeriodeContentBudgetText(simulation)} va être rendu${simulation?.budget?.type === TypeBudget.DECISION_MODIFICATIVE ? 'e' : ''} exécutoire.`}
					<br />
					Les montants votés alimentent les crédits disponibles pour l'exécution
					budgétaire.
				</>
			);
		}
		case TypePeriode.CA_CFU:
			return <>Les restes à réaliser vont être validés.</>;
		default:
			return '';
	}
};

const NextPeriodeModal = ({
	onClose,
	onValidate,
	open,
	simulation,
	title,
}: PeriodeModalProps) => {
	const [count, setCount] = React.useState<number>(0);
	const [loading, setLoading] = React.useState<boolean>(false);
	const [shouldResetMontant, setShouldResetMontant] =
		React.useState<boolean>(false);
	const [job, setJob] = React.useState<Job>();
	const handleFetchMontantCount = async () => {
		if (simulation?.typePeriode === TypePeriode.PROPOSE) {
			try {
				setCount(
					await getMontantSimulationNotSamePropVoteCount(
						simulation?.identifiant,
					),
				);
			} catch (e) {
				toast.error(
					getErrorMessage(
						e,
						'Erreur lors de la récupération du nombre de montants ayant une proposition différente du vote',
					),
				);
			}
		}
	};

	const handleCheckboxChange = (_e, { checked }: CheckboxProps) =>
		setShouldResetMontant(checked);

	const handleValidate = async () => {
		if (simulation?.typePeriode === TypePeriode.DEMANDE) {
			onValidate(TypePeriode.PROPOSE);
			return;
		}

		const canTransfertCompta = [TypePeriode.CA_CFU, TypePeriode.VOTE].includes(
			simulation?.typePeriode,
		);

		try {
			setLoading(true);
			if (simulation?.typePeriode === TypePeriode.PROPOSE) {
				await propToVote(simulation?.identifiant, shouldResetMontant);
				setLoading(false);
				onValidate(TypePeriode.VOTE);
			} else if (canTransfertCompta) {
				const newJob = await transfertComptabilite(simulation?.identifiant);
				setJob(newJob);
			}
		} catch (e) {
			let errorText = '';
			if (simulation?.typePeriode === TypePeriode.PROPOSE) {
				errorText = 'Erreur lors du vote';
				setLoading(false);
			} else if (canTransfertCompta) {
				errorText = 'Erreur lors du transfert en comptabilité';
			}
			toast.error(getErrorMessage(e, errorText));
		}
	};

	const handleCloseJob = (finishedJob: Job) => {
		setJob(null);
		setLoading(false);

		if (finishedJob.status === 1) {
			// Apres le transfert en compta, on reste dans tous les cas, soit en periode CA_CFU, soit en VOTE
			onValidate(simulation.typePeriode);
		} else {
			toast.error(finishedJob.resultat);
		}
	};

	const handleJobError = () => {
		setJob(null);
		setLoading(false);
		toast.error('Erreur lors du traitement');
	};

	return (
		<>
			<Modal
				closeIcon
				onClose={onClose}
				onMount={handleFetchMontantCount}
				open={open}
			>
				<Modal.Header content={title} />
				<Modal.Content>
					{renderPeriodeContent(simulation)}
					{simulation?.typePeriode === TypePeriode.PROPOSE && count > 0 && (
						<>
							<br />
							<br />
							<Checkbox
								checked={shouldResetMontant}
								onChange={handleCheckboxChange}
								label={`Réinitialiser le montant des ${count}
                lignes budgétaires comportant déjà un montant voté différent de la proposition.`}
								toggle
							/>
						</>
					)}
				</Modal.Content>
				<Modal.Actions>
					<Button
						content="Vers la période suivante"
						data-testid="confirmButton"
						disabled={loading}
						loading={loading}
						onClick={handleValidate}
						positive
					/>
				</Modal.Actions>
			</Modal>
			{loading && job && (
				<TraitementLong
					title={title}
					identifiantJob={job?.identifiant}
					onFinish={handleCloseJob}
					onError={handleJobError}
				/>
			)}
		</>
	);
};

export default NextPeriodeModal;
